import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { parseCSVFile, parseExcelFile } from '../../../services/csvAnalysisService';
import { importWorkOrdersToSupabase, getWorkOrderCount, clearWorkOrdersTable } from '../../../services/workOrderService';

const WorkOrderImport = () => {
  const [recordCount, setRecordCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState(null); // { type: 'success'|'error'|'info', message: string }
  const [progress, setProgress] = useState(null); // { current: number, total: number }
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCount();
  }, []);

  const loadCount = async () => {
    const count = await getWorkOrderCount();
    setRecordCount(count);
  };

  const handleFileSelect = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const ext = file?.name?.split('.')?.pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls']?.includes(ext)) {
      setImportStatus({ type: 'error', message: 'Please upload a CSV or Excel (.xlsx, .xls) file.' });
      return;
    }

    setIsLoading(true);
    setImportStatus({ type: 'info', message: `Parsing ${file?.name}...` });
    setProgress(null);

    try {
      let parsed;
      if (ext === 'csv') {
        parsed = await parseCSVFile(file);
      } else {
        parsed = await parseExcelFile(file);
      }

      if (!parsed?.data || parsed?.data?.length === 0) {
        setImportStatus({ type: 'error', message: 'No data rows found in the file. Please check the file format.' });
        setIsLoading(false);
        return;
      }

      setImportStatus({ type: 'info', message: `Importing ${parsed?.data?.length} records into Supabase...` });

      const result = await importWorkOrdersToSupabase(parsed?.data, (current, total) => {
        setProgress({ current, total });
      });

      if (result?.errors?.length > 0 && result?.inserted === 0) {
        setImportStatus({ type: 'error', message: `Import failed: ${result?.errors?.[0]}` });
      } else if (result?.errors?.length > 0) {
        setImportStatus({
          type: 'info',
          message: `Imported ${result?.inserted} records with ${result?.errors?.length} batch error(s). Check console for details.`
        });
      } else {
        setImportStatus({
          type: 'success',
          message: `✅ Successfully imported ${result?.inserted} work order records into Supabase. Analytics and AI Suggestions will now use this data.`
        });
      }

      await loadCount();
    } catch (err) {
      console.error('Import error:', err);
      setImportStatus({ type: 'error', message: `Import failed: ${err?.message}` });
    } finally {
      setIsLoading(false);
      setProgress(null);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleClearTable = async () => {
    setShowClearConfirm(false);
    setIsLoading(true);
    setImportStatus({ type: 'info', message: 'Clearing work order data...' });

    const result = await clearWorkOrdersTable();
    if (result?.success) {
      setImportStatus({ type: 'success', message: '✅ Work order data cleared. You can now import a fresh dataset.' });
      setRecordCount(0);
    } else {
      setImportStatus({ type: 'error', message: `Failed to clear data: ${result?.error}` });
    }
    setIsLoading(false);
  };

  const statusColors = {
    success: 'bg-success/10 border-success/30 text-success',
    error: 'bg-error/10 border-error/30 text-error',
    info: 'bg-primary/10 border-primary/30 text-primary',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">Work Order Data Import</h2>
        <p className="text-sm text-muted-foreground">
          Upload your Excel or CSV work order file to push all records into Supabase. Analytics and AI Suggestions will query this data directly for full aggregations.
        </p>
      </div>
      {/* Current Record Count */}
      <div className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Database" size={22} className="text-primary" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Records in Supabase</p>
          <p className="text-2xl font-bold text-foreground">{recordCount?.toLocaleString()}</p>
        </div>
        <button
          onClick={loadCount}
          className="ml-auto p-2 rounded-lg hover:bg-accent/10 transition-colors"
          title="Refresh count"
        >
          <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
        </button>
      </div>
      {/* Expected Columns */}
      <div className="p-4 bg-accent/5 rounded-lg border border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Expected Columns</p>
        <div className="flex flex-wrap gap-2">
          {['Date', 'Shift', 'Shift Incharge', 'Equipment ID', 'Occupance', 'Work Order Type', 'Breakdown Time (mins)', 'Description', 'Solution', 'Spare Consumed', 'Status', 'Why-Why Analysis', 'Root Cause']?.map(col => (
            <span key={col} className="text-xs px-2 py-1 rounded-full bg-card border border-border text-muted-foreground">
              {col}
            </span>
          ))}
        </div>
      </div>
      {/* Upload Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Upload" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Upload Work Order File</h3>
            <p className="text-xs text-muted-foreground">Supports .xlsx, .xls, and .csv formats</p>
          </div>
        </div>

        {/* Progress bar */}
        {progress && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Importing...</span>
              <span>{progress?.current} / {progress?.total}</span>
            </div>
            <div className="w-full bg-border rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((progress?.current / progress?.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Status message */}
        {importStatus && (
          <div className={`mb-4 p-3 rounded-lg border text-sm ${statusColors?.[importStatus?.type]}`}>
            {importStatus?.message}
          </div>
        )}

        <label htmlFor="work-order-import-file">
          <Button
            variant="default"
            iconName="Upload"
            fullWidth
            disabled={isLoading}
            asChild
          >
            <span>{isLoading ? 'Processing...' : 'Select File to Import'}</span>
          </Button>
          <input
            id="work-order-import-file"
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isLoading}
          />
        </label>

        <p className="text-xs text-muted-foreground mt-3 text-center">
          Large files are imported in batches of 500 records. Existing records are preserved — upload again to append more data.
        </p>
      </div>
      {/* Clear Data Section */}
      {recordCount > 0 && (
        <div className="bg-error/5 rounded-lg border border-error/20 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-error/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={18} className="text-error" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground">Clear Work Order Data</h3>
              <p className="text-xs text-muted-foreground">Remove all {recordCount?.toLocaleString()} records from Supabase before re-importing</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Trash2"
            onClick={() => setShowClearConfirm(true)}
            disabled={isLoading}
          >
            Clear All Work Order Records
          </Button>
        </div>
      )}
      {/* Clear Confirm Dialog */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-foreground">Clear Work Order Data?</h3>
                <p className="text-sm text-muted-foreground">This will delete all {recordCount?.toLocaleString()} records</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              This permanently removes all work order records from Supabase. Analytics and AI Suggestions will show no data until you re-import. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowClearConfirm(false)} fullWidth>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleClearTable} fullWidth>
                Clear All Records
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkOrderImport;
